// src/store/authStore.ts
import {create} from "zustand";
import {IAssociateUsers} from "../models/interfaces/associateUsers.interface.ts";
import {IUser} from "../models/interfaces/user.interface.ts";
import {
  IWorkspaceLanguageConfiguration,
  IWorkspaceLanguageConfigurationDTO
} from "../models/interfaces/workspaceLanguageConfiguration.interface.ts";
import {
  createNewProjectConfiguration, deleteNewProjectConfiguration, editNewProjectConfiguration,
  getCustomConfigurations,
  getUserNewProjectCreationConfigurations
} from "../services/userService.ts";
import {UserPreferences} from "../models/interfaces/userPreferences.ts";
import {NewProjectConfigurationDTO} from "../models/interfaces/dtos/newProjectConfigurations.dto.ts";
import {
  transformNewProjectConfigurationDTO,
  transformNewProjectConfigurationsDto
} from "../utils/transformers/userTransformer.ts";
import {NewProjectConfiguration, NewProjectConfigurationConfig} from "../models/interfaces/newProjectConfigurations.ts";

export interface IUserState {
  user: IUser | null;
  associateUsers: IAssociateUsers;
  languageConfig: IWorkspaceLanguageConfiguration
  preferences: UserPreferences;
  initUser: (user: IUser) => void;
  removeUser: () => void;
  getLanguageConfig: () => Promise<void>;
  getUserPreferences: () => Promise<void>;
  addNewProjectCreationConfiguration: (name: string,sourceLanguage: string, configuration: NewProjectConfigurationConfig) => Promise<void>;
  editNewProjectCreationConfiguration: (configId: string, name: string, configuration: NewProjectConfigurationConfig) => Promise<void>;
  removeNewProjectCreationConfiguration: (configId: string) => Promise<void>;
}

const useUserStore = create<IUserState>((set, get) => ({
  user: null,
  associateUsers: {} as IAssociateUsers,
  languageConfig: {} as IWorkspaceLanguageConfiguration,
  preferences: {} as UserPreferences,
  initUser: (user: IUser) =>
    set(() => ({
      user: user,
    })),
  removeUser: () => set(() => ({user: null})),
  getLanguageConfig: async () => {
    try {
      const languageConfigDto: IWorkspaceLanguageConfigurationDTO[] = await getCustomConfigurations();
      const customLanguageConfig: IWorkspaceLanguageConfiguration = {} as IWorkspaceLanguageConfiguration
      languageConfigDto.forEach((langConfig: IWorkspaceLanguageConfigurationDTO) => {
        customLanguageConfig[langConfig.language] = langConfig
      });
      set(() => ({languageConfig: customLanguageConfig}))
    } catch (error) {
      console.error('Error fetching custom language configurations:', error);
    }
  },
  getUserPreferences: async () => {
    try {
      const results: [PromiseSettledResult<NewProjectConfigurationDTO[]>] = await Promise.allSettled([
        getUserNewProjectCreationConfigurations(),
      ]);

      const [newProjectConfigurationDto] = results

      const next: Partial<UserPreferences> = {
        newProjectCreation: newProjectConfigurationDto.status == 'fulfilled'
          ? transformNewProjectConfigurationsDto(newProjectConfigurationDto.value || [])
          : undefined
      };

      set(() => ({
        preferences: {
          ...get().preferences,
          ...next
        }
      }));
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  },
  addNewProjectCreationConfiguration: async (name: string,sourceLanguage:string, configuration: NewProjectConfigurationConfig) => {
    try {
      const projectCreationConfigDto: NewProjectConfigurationDTO = await createNewProjectConfiguration(name,sourceLanguage, configuration);
      const newProjectCreationConfig: NewProjectConfiguration = transformNewProjectConfigurationDTO(projectCreationConfigDto);
      set((state) => ({
        preferences: {
          ...state.preferences,
          newProjectCreation: {
            ...state.preferences.newProjectCreation,
            [newProjectCreationConfig.id]: newProjectCreationConfig
          }
        }
      }));
    } catch (error) {
      console.error('Error adding new project creation configuration:', error);
      throw error;
    }
  },
  editNewProjectCreationConfiguration: async (configId: string, name: string, configuration: NewProjectConfigurationConfig) => {
    try {
      const projectCreationConfigDto: NewProjectConfigurationDTO = await editNewProjectConfiguration(configId, name, configuration);
      const newProjectCreationConfig: NewProjectConfiguration = transformNewProjectConfigurationDTO(projectCreationConfigDto);
      set((state) => ({
        preferences: {
          ...state.preferences,
          newProjectCreation: {
            ...state.preferences.newProjectCreation,
            [newProjectCreationConfig.id]: newProjectCreationConfig
          }
        }
      }));
    } catch (error) {
      console.error('Error adding new project creation configuration:', error);
      throw error;
    }
  },
  removeNewProjectCreationConfiguration: async (configId: string) => {
    try {
      await deleteNewProjectConfiguration(configId);
      set((state) => {
        const updatedPreferences = {...state.preferences};
        if (updatedPreferences.newProjectCreation) {
          delete updatedPreferences.newProjectCreation[configId];
        }
        return {preferences: updatedPreferences};
      });
    } catch (error) {
      console.error('Error removing new project creation configuration:', error);
      throw error;
    }
  },
}));

export default useUserStore;